import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import MobileNotice from "./components/MobileNotice";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import ScrollToHash from "./components/ScrollToHash";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import CheckoutPage from "./pages/CheckoutPage";
import BookingPage from "./pages/BookingPage";
import BookingStatusPage from "./pages/BookingStatusPage";
import StorePage from "./pages/StorePage";
import PurchaseSuccessPage from "./pages/PurchaseSuccessPage";
import ServiceRequestPage from "./pages/ServiceRequestPage";
import RequestSuccessPage from "./pages/RequestSuccessPage";
import ClientDashboardPage from "./pages/ClientDashboardPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminRequestsPage from "./pages/AdminRequestsPage";
import AdminRequestDetailPage from "./pages/AdminRequestDetailPage";
import QuotationRequestPage from "./pages/QuotationRequestPage";
import QuotationSuccessPage from "./pages/QuotationSuccessPage";
import ContactPage from "./pages/ContactPage";
import ImprovementPlanRequestPage from "./pages/ImprovementPlanRequestPage";
import WorkplaceNurseriesPage from "./pages/WorkplaceNurseriesPage";
import AboutPage from "./pages/AboutPage";
import ProgramsPage from "./pages/ProgramsPage";
import MontessoriProgramPage from "./pages/MontessoriProgramPage";
import LegalPage from "./pages/LegalPage";
import TalentBankPage from "./pages/TalentBankPage";
import CreateProfilePage from "./pages/CreateProfilePage";
import AdminTalentBankPage from "./pages/AdminTalentBankPage";
import KnowledgeCenterPage from "./pages/KnowledgeCenterPage";
import KnowledgeArticlePage from "./pages/KnowledgeArticlePage";
import ToolComingSoonPage from "./pages/ToolComingSoonPage";
import OpsKnowledgePage from "./ops/OpsKnowledgePage";
import OpsKnowledgeEditorPage from "./ops/OpsKnowledgeEditorPage";
import OpsNewsletterPage from "./ops/OpsNewsletterPage";
import { Toaster } from "./components/ui/sonner";
import { OpsAuthProvider } from "./ops/OpsAuthContext";
import { OpsProtectedRoute } from "./ops/OpsProtectedRoute";
import OpsLoginPage from "./ops/OpsLoginPage";
import OpsHomePage from "./ops/OpsHomePage";
import OpsInboxPage from "./ops/OpsInboxPage";
import OpsSettingsPage from "./ops/OpsSettingsPage";
import OpsProjectsPage from "./ops/OpsProjectsPage";
import OpsProjectDetailPage from "./ops/OpsProjectDetailPage";
import OpsMyTasksPage from "./ops/OpsMyTasksPage";
import OpsTeamPage from "./ops/OpsTeamPage";
import OpsTeamAnalyticsPage from "./ops/OpsTeamAnalyticsPage";
import OpsApprovalsPage from "./ops/OpsApprovalsPage";

// Renders Navbar/Footer only on public routes; hides them under /ops/*
function PublicChrome({ children }) {
  const { pathname } = useLocation();
  const isOps = pathname.startsWith("/ops");
  return (
    <>
      {!isOps && <MobileNotice />}
      {!isOps && <Navbar />}
      {children}
      {!isOps && <FloatingWhatsApp />}
      {!isOps && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#FAFAF7]">
      <BrowserRouter>
        <OpsAuthProvider>
          <ScrollToHash />
          <PublicChrome>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/booking/status" element={<BookingStatusPage />} />
              <Route path="/booking/status/:ref" element={<BookingStatusPage />} />
              <Route path="/store" element={<StorePage />} />
              <Route path="/tools/:toolId/coming-soon" element={<ToolComingSoonPage />} />
              <Route path="/purchase/success" element={<PurchaseSuccessPage />} />
              <Route path="/services/request" element={<ServiceRequestPage />} />
              <Route path="/services/request/success" element={<RequestSuccessPage />} />
              <Route path="/client/:token" element={<ClientDashboardPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin/requests" element={<AdminRequestsPage />} />
              <Route path="/admin/requests/:id" element={<AdminRequestDetailPage />} />
              <Route path="/quotation" element={<QuotationRequestPage />} />
              <Route path="/quotation/success" element={<QuotationSuccessPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/services/improvement-plan" element={<ImprovementPlanRequestPage />} />
              <Route path="/sectors/workplace-nurseries" element={<WorkplaceNurseriesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/programs" element={<ProgramsPage />} />
              <Route path="/programs/montessori-applied" element={<MontessoriProgramPage />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/talent-bank" element={<TalentBankPage />} />
              <Route path="/talent-bank/create-profile" element={<CreateProfilePage />} />
              <Route path="/admin/talent-bank" element={<AdminTalentBankPage />} />

              {/* Knowledge Center (public) */}
              <Route path="/knowledge" element={<KnowledgeCenterPage />} />
              <Route path="/knowledge/:slug" element={<KnowledgeArticlePage />} />

              {/* Ops (internal operating system) */}
              <Route path="/ops/login" element={<OpsLoginPage />} />
              <Route
                path="/ops"
                element={
                  <OpsProtectedRoute>
                    <OpsHomePage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/inbox"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director", "operations_manager"]}>
                    <OpsInboxPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/newsletter"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director", "operations_manager"]}>
                    <OpsNewsletterPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/settings"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director"]}>
                    <OpsSettingsPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/projects"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director", "operations_manager", "track_leader"]}>
                    <OpsProjectsPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/projects/:id"
                element={
                  <OpsProtectedRoute>
                    <OpsProjectDetailPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/my-tasks"
                element={
                  <OpsProtectedRoute>
                    <OpsMyTasksPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/team"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director"]}>
                    <OpsTeamPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/analytics/team"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director", "operations_manager"]}>
                    <OpsTeamAnalyticsPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/approvals"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director"]}>
                    <OpsApprovalsPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/knowledge"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director"]}>
                    <OpsKnowledgePage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/knowledge/new"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director"]}>
                    <OpsKnowledgeEditorPage />
                  </OpsProtectedRoute>
                }
              />
              <Route
                path="/ops/knowledge/:id"
                element={
                  <OpsProtectedRoute allowedRoles={["executive_director"]}>
                    <OpsKnowledgeEditorPage />
                  </OpsProtectedRoute>
                }
              />
            </Routes>
          </PublicChrome>
          <Toaster position="top-center" />
        </OpsAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
